import { Trans } from '@lingui/react/macro';
import { CheckCircle } from '@mui/icons-material';
import { CircularProgress } from '@mui/material';
import { Button } from '@repo/videa-components/src/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@repo/videa-components/src/ui/tooltip';
import { Circle, Info, X, XCircle } from 'lucide-react';

import { useGetCountry } from '@/utils/internationalization/useGetCountry';

import { VideaTypography } from '../../../../../common-components/elements/VideaTypography/VideaTypography';
import { useGetTaskDisplayName } from '../../../../../react-query/useGetTasks';
import type { Task, TaskStatus } from '../../../../../react-query/useGetTasks';

const TASK_ICON_SIZE = 20;

interface GamifiedTaskProps {
  disabled?: boolean;
  isBlackAndWhite?: boolean;
  isLoading?: boolean;
  onClick?: (task: Task, status: TaskStatus) => void;
  task: Task;
}

export const GamifiedTaskRedesigned = ({
  disabled = false,
  isBlackAndWhite = false,
  isLoading,
  onClick,
  task,
}: GamifiedTaskProps) => {
  const getTaskDisplayName = useGetTaskDisplayName();
  const isCanadianPractice = useGetCountry() === 'CA';

  const handleClickAccept = () => {
    onClick?.(
      task,
      task.status === 'ACKNOWLEDGED' ? 'SUGGESTED' : 'ACKNOWLEDGED',
    );
  };

  const handleClickReject = () => {
    onClick?.(task, task.status === 'REJECTED' ? 'SUGGESTED' : 'REJECTED');
  };

  if (task.status === 'NOT_SUGGESTED') return null;

  const reviewIconColor = isBlackAndWhite
    ? 'vai-text-black'
    : 'vai-text-emerald-500';
  const rejectedIconColor = isBlackAndWhite
    ? 'vai-text-black'
    : 'vai-text-emerald-200';
  const ignoreIconColor = isBlackAndWhite
    ? 'vai-text-black'
    : 'vai-text-blaze-500';
  const acknowledgedIconColor = isBlackAndWhite
    ? 'vai-text-black'
    : 'vai-text-blaze-300';

  return (
    <div
      className={`vai-inline-flex vai-items-center vai-bg-transparent vai-border-0 vai-py-1 vai-px-2 disabled:vai-cursor-not-allowed vai-text-slate-800`}
      data-cy={task.type}
      data-testid={task.type}
    >
      {isLoading ? (
        <CircularProgress
          className="vai-text-gray-700 vai-h-5 vai-w-5"
          size={TASK_ICON_SIZE}
        />
      ) : (
        <>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="outline"
                size="icon"
                className={`vai-flex vai-h-5 vai-w-5 vai-items-center vai-rounded-full vai-border-none vai-bg-transparent vai-cursor-pointer ${reviewIconColor}`}
                onClick={handleClickAccept}
                aria-label="Task Status Button"
                disabled={task.status === 'COMPLETED' || disabled}
              >
                {task.status === 'SUGGESTED' && (
                  <Circle className={`vai-h-5 vai-w-5 ${reviewIconColor}`} />
                )}
                {(task.status === 'ACKNOWLEDGED' ||
                  task.status === 'COMPLETED') && (
                  <CheckCircle
                    className={`vai-h-5 vai-w-5 ${reviewIconColor}`}
                  />
                )}
                {task.status === 'REJECTED' && (
                  <Circle
                    className={`vai-h-5 vai-w-5 ${rejectedIconColor}`}
                  />
                )}
              </Button>
            </TooltipTrigger>
            <TooltipContent className="vai-flex vai-items-center vai-justify-center vai-px-2 vai-py-1 vai-rounded-md vai-text-sm vai-shadow-md vai-bg-navy-500 vai-text-grey-200">
              <CheckCircle className="vai-mr-1 vai-h-4 vai-w-4" />
              {task.status === 'COMPLETED' ? 'Completed' : 'Reviewed'}
            </TooltipContent>
          </Tooltip>
          {task.status !== 'COMPLETED' && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="outline"
                  size="icon"
                  className={`vai-flex vai-h-5 vai-w-5 vai-items-center vai-p-0.5 vai-rounded-full vai-border-none vai-bg-transparent vai-cursor-pointer ${ignoreIconColor}`}
                  onClick={handleClickReject}
                  aria-label="Task Reject Button"
                  disabled={disabled}
                >
                  {task.status === 'SUGGESTED' && (
                    <X className={`vai-h-5 vai-w-5 ${ignoreIconColor}`} />
                  )}
                  {task.status === 'ACKNOWLEDGED' && (
                    <X
                      className={`vai-h-5 vai-w-5 ${acknowledgedIconColor}`}
                    />
                  )}
                  {task.status === 'REJECTED' && (
                    <X className={`vai-h-5 vai-w-5 ${ignoreIconColor}`} />
                  )}
                </Button>
              </TooltipTrigger>
              <TooltipContent
                side="top"
                className="vai-flex vai-items-center vai-justify-center vai-px-2 vai-py-1 vai-rounded-md vai-text-sm vai-shadow-md vai-bg-navy-500 vai-text-grey-200"
              >
                <XCircle className="vai-mr-1 vai-h-4 vai-w-4" />
                Ignore
              </TooltipContent>
            </Tooltip>
          )}
        </>
      )}
      {task.type == 'BW_DUE' &&
      isCanadianPractice &&
      task.status !== 'COMPLETED' ? (
        <Tooltip>
          <TooltipTrigger asChild>
            <VideaTypography
              className={`vai-text-left vai-text-sm vai-font-light vai-font-inter ${
                task.status === 'REJECTED' ? 'vai-text-stone-400' : ''
              }`}
            >
              {getTaskDisplayName(task.type)}
            </VideaTypography>
          </TooltipTrigger>
          <TooltipContent
            side="top"
            className="vai-flex vai-items-center vai-justify-center vai-px-3 vai-py-2 vai-rounded-md vai-text-xs vai-shadow-md vai-text-white vai-bg-[#222630]/75"
          >
            <span className="vai-text-slate-800 vai-flex vai-items-center">
              <Trans>
                <Info
                  fontSize="inherit"
                  className="vai-mr-1 vai-h-4 vai-w-4 "
                />
                Bitewings have not been taken in the last 12 months. Identify
                patient risk factors before prescribing bitewings.
              </Trans>
            </span>
          </TooltipContent>
        </Tooltip>
      ) : (
        <VideaTypography
          className={`vai-text-left vai-text-sm vai-leading-4 vai-font-light vai-font-inter ${
            task.status === 'REJECTED'
              ? 'vai-text-stone-400 vai-line-through'
              : ''
          } `}
        >
          {getTaskDisplayName(task.type)}
        </VideaTypography>
      )}
    </div>
  );
};
